import clsx from "clsx";
import { Crosshair, Trash2 } from "lucide-react";
import { type ReactNode, type Ref, useEffect, useRef, useState } from "react";

import type { AgentKind, LineKind, ZoneKind } from "../api/types";
import { PropRow } from "../components/panel";
import { SaveDossierHint } from "../components/SaveDossierHint";
import { Button, Field, FloatingPanel, inputClass } from "../components/ui";
import { k, lang, locale, t } from "../i18n/i18n";
import { term } from "../i18n/vocabulary";
import { hazardDistance, lengthM, type LonLat, ringAreaKm2 } from "../map/graphics/geometry";
import { GraphicGlyph } from "../map/graphics/GraphicGlyph";
import {
  findGraphic,
  labelOf,
  RADIUS_RANGE,
  relabel,
  typeOf,
  useGraphics,
  viewOf,
  WIND_RANGE,
} from "../map/graphics/useGraphics";
import { formatMgrs } from "../map/MapCanvas";
import { useUi } from "../state/ui";
import { Select } from "../components/Select";
import { CardActions, CardFooter, CardTitle } from "./cardParts";
import { refuseIfDossierLocked } from "../sharing/access";

/** Fiche d'un graphique tactique : nom, type, rayon d'une zone, agent et météo d'un panache NRBC (saisie à la main,
 *  comme sur l'iPad), repères MGRS, suppression. Chaque modification est enregistrée dans le dossier et
 *  s'annule depuis l'avis. */

const KM = (m: number, digits = 1) => `${(m / 1000).toLocaleString(locale(), { maximumFractionDigits: digits })} km`;

/** Rose des vents en 16 secteurs (O pour ouest ; W en anglais). */
const CARDINALS = [
  k("N"), k("NNE"), k("NE"), k("ENE"), k("E"), k("ESE"), k("SE"), k("SSE"),
  k("S"), k("SSO"), k("SO"), k("OSO"), k("O"), k("ONO"), k("NO"), k("NNO"),
];
function cardinal(bearing: number): string {
  return t(CARDINALS[Math.round((((bearing % 360) + 360) % 360) / 22.5) % 16]!);
}

/** Nombre saisi au format de la langue affichée : « 1 500,5 » en français, « 1,500.5 » en anglais. */
function parseNumber(text: string): number {
  const compact = text.replace(/[\s  ]/g, "");
  return Number(lang() === "en" ? compact.replace(/,/g, "") : compact.replace(",", "."));
}

/** Champ enregistré à la validation (Entrée ou sortie du champ) ; Échap rend la valeur enregistrée. Exporté pour
 *  les autres fiches (GDH saisi d'une LIMA) : `onInput` suit la saisie en cours (aperçu en direct), `error`
 *  affiche un refus venu d'ailleurs (message du serveur), `inputRef` permet de désigner le champ. */
export function CommitInput({
  value,
  onCommit,
  validate,
  disabled,
  placeholder,
  maxLength,
  unit,
  numeric,
  onInput,
  error: outsideError,
  inputRef,
  ariaLabel,
  showError = true,
}: {
  value: string;
  onCommit: (text: string) => void;
  validate?: (text: string) => string | null;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  unit?: string;
  numeric?: boolean;
  onInput?: (text: string) => void;
  error?: string | null;
  inputRef?: Ref<HTMLInputElement>;
  ariaLabel?: string;
  /** Message d'erreur sous le champ (false : l'appelant l'affiche lui-même, le champ reste cerné de rouge). */
  showError?: boolean;
}) {
  const [text, setText] = useState(value);
  const [ownError, setError] = useState<string | null>(null);
  const error = ownError ?? outsideError ?? null;
  const cancelled = useRef(false);
  useEffect(() => {
    setText(value);
    setError(null);
    onInput?.(value);
    // `onInput` recréé à chaque rendu : seule la valeur enregistrée relance la synchronisation.
  }, [value]);
  const commit = () => {
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    const problem = validate?.(text) ?? null;
    setError(problem);
    if (!problem && text.trim() !== value.trim()) onCommit(text);
  };
  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          className={clsx(inputClass, unit && "pr-14", numeric && "tabular-nums", error && "border-danger")}
          value={text}
          disabled={disabled}
          placeholder={placeholder}
          maxLength={maxLength}
          inputMode={numeric ? "decimal" : undefined}
          aria-invalid={error ? true : undefined}
          aria-label={ariaLabel}
          onChange={(e) => {
            setText(e.target.value);
            onInput?.(e.target.value);
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") {
              // Appui consommé : la pile d'Échap (désélection, sortie du mode édition) ne le traite pas aussi.
              e.preventDefault();
              cancelled.current = true;
              setText(value);
              setError(null);
              onInput?.(value);
              e.currentTarget.blur();
            }
          }}
        />
        {unit && (
          <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-ui text-ink-muted">
            {unit}
          </span>
        )}
      </div>
      {error && showError && <span className="mt-1 block text-meta text-danger">{error}</span>}
    </div>
  );
}

function rangeCheck([min, max]: [number, number], unit: string) {
  return (text: string) => {
    const n = parseNumber(text);
    return Number.isFinite(n) && n >= min && n <= max
      ? null
      : t("Entre {min} et {max} {unit}", { min: min.toLocaleString(locale()), max: max.toLocaleString(locale()), unit });
  };
}

export function GraphicCard({ id }: { id: string }) {
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const requestSave = useUi((s) => s.requestSave);
  const { graphics, catalog, canEdit, edit, remove } = useGraphics();
  const item = findGraphic(graphics, id);
  if (!item) return null;
  const type = typeOf(catalog, item);
  const label = labelOf(catalog, item);
  const disabled = !canEdit;

  let fields: ReactNode = null;
  let props: ReactNode = null;

  if (item.family === "operationalGraphics") {
    const zone = item.graphic;
    fields = (
      <>
        <Field label={t("Type")}>
          <Select
            className={inputClass}
            value={zone.kind}
            disabled={disabled}
            onChange={(e) => {
              const kind = e.target.value as ZoneKind;
              edit(
                id,
                (it, all) => {
                  if (it.family !== "operationalGraphics") return it.graphic;
                  const from = catalog?.zones.find((z) => z.kind === it.graphic.kind);
                  const to = catalog?.zones.find((z) => z.kind === kind);
                  // Rayon par défaut de l'ancien type : remplacé par celui du nouveau ; rayon saisi : gardé.
                  const radius = from && to && it.graphic.radiusMetres === from.radius_m ? to.radius_m : it.graphic.radiusMetres;
                  const next = relabel(it.graphic.label, all, "operationalGraphics", from?.short ?? "", to?.short ?? kind);
                  return { ...it.graphic, kind, radiusMetres: radius, label: next };
                },
                t("Type modifié"),
              );
            }}
          >
            {catalog?.zones.map((z) => (
              <option key={z.kind} value={z.kind}>
                {term(z.name)} ({z.short})
              </option>
            ))}
          </Select>
        </Field>
        {zone.ring ? (
          <Field label={t("Contour")}>
            <p className="py-1.5 text-ui text-ink-soft">
              {t("Importé, {n} sommets ; se déplace d'un bloc.", { n: zone.ring.length })}
            </p>
          </Field>
        ) : (
          <Field label={t("Rayon")}>
            <CommitInput
              numeric
              unit="m"
              value={zone.radiusMetres.toLocaleString(locale(), { maximumFractionDigits: 0 })}
              disabled={disabled}
              validate={rangeCheck(RADIUS_RANGE, "m")}
              onCommit={(text) =>
                edit(id, (it) => ({ ...it.graphic, radiusMetres: Math.round(parseNumber(text)) }), t("Rayon modifié"))
              }
            />
          </Field>
        )}
      </>
    );
    props = (
      <>
        <PropRow label={t("Centre")} mono>
          {formatMgrs(zone.longitude, zone.latitude)}
        </PropRow>
        <PropRow label={t("Surface")}>
          {(zone.ring
            ? ringAreaKm2(zone.ring.map((c): LonLat => [c.longitude, c.latitude]))
            : Math.PI * (zone.radiusMetres / 1000) ** 2
          ).toLocaleString(locale(), { maximumFractionDigits: 1 })}{" "}
          km²
        </PropRow>
      </>
    );
  } else if (item.family === "lineGraphics") {
    const line = item.graphic;
    const points: LonLat[] = line.coordinates.map((c) => [c.longitude, c.latitude]);
    const first = points[0]!;
    const last = points[points.length - 1]!;
    fields = (
      <Field label={t("Type")}>
        <Select
          className={inputClass}
          value={line.kind}
          disabled={disabled}
          onChange={(e) => {
            const kind = e.target.value as LineKind;
            edit(
              id,
              (it, all) => {
                if (it.family !== "lineGraphics") return it.graphic;
                const from = catalog?.lines.find((l) => l.kind === it.graphic.kind);
                const to = catalog?.lines.find((l) => l.kind === kind);
                const next = relabel(it.graphic.label, all, "lineGraphics", from?.short ?? "", to?.short ?? kind);
                return { ...it.graphic, kind, label: next };
              },
              t("Type modifié"),
            );
          }}
        >
          {catalog?.lines.map((l) => (
            <option key={l.kind} value={l.kind}>
              {term(l.name)} ({l.short})
            </option>
          ))}
        </Select>
      </Field>
    );
    props = (
      <>
        <PropRow label={t("Longueur")}>{KM(lengthM(points))}</PropRow>
        <PropRow label={t("Points")}>{points.length}</PropRow>
        <PropRow label={t("Début")} mono>
          {formatMgrs(first[0], first[1])}
        </PropRow>
        <PropRow label={line.kind === "axisOfAdvance" ? t("Pointe") : t("Fin")} mono>
          {formatMgrs(last[0], last[1])}
        </PropRow>
      </>
    );
  } else {
    const plume = item.graphic;
    const agent = catalog?.agents.find((a) => a.kind === plume.agent);
    const reach = agent ? hazardDistance(agent.downwind_m, plume.windSpeedKnots) : 0;
    fields = (
      <>
        <Field label={t("Agent")}>
          <Select
            className={inputClass}
            value={plume.agent}
            disabled={disabled}
            onChange={(e) => {
              const kind = e.target.value as AgentKind;
              edit(
                id,
                (it, all) => {
                  if (it.family !== "cbrnPlumes") return it.graphic;
                  const from = catalog?.agents.find((a) => a.kind === it.graphic.agent);
                  const to = catalog?.agents.find((a) => a.kind === kind);
                  const next = relabel(it.graphic.label, all, "cbrnPlumes", `NRBC ${from?.letter ?? ""}`, `NRBC ${to?.letter ?? "?"}`);
                  return { ...it.graphic, agent: kind, label: next };
                },
                t("Agent modifié"),
              );
            }}
          >
            {catalog?.agents.map((a) => (
              <option key={a.kind} value={a.kind}>
                {term(a.name)} ({a.letter})
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t("Dérive vers ({direction})", { direction: cardinal(plume.plumeBearingDegrees) })}>
            <CommitInput
              numeric
              unit="°"
              value={String(Math.round(plume.plumeBearingDegrees))}
              disabled={disabled}
              validate={rangeCheck([0, 360], "°")}
              onCommit={(text) =>
                edit(id, (it) => ({ ...it.graphic, plumeBearingDegrees: Math.round(parseNumber(text)) % 360 }), t("Direction modifiée"))
              }
            />
          </Field>
          <Field label={t("Vent")}>
            <CommitInput
              numeric
              unit={t("nœuds")}
              value={plume.windSpeedKnots.toLocaleString(locale(), { maximumFractionDigits: 1 })}
              disabled={disabled}
              validate={rangeCheck(WIND_RANGE, t("nœuds"))}
              onCommit={(text) =>
                edit(id, (it) => ({ ...it.graphic, windSpeedKnots: parseNumber(text) }), t("Vent modifié"))
              }
            />
          </Field>
        </div>
      </>
    );
    props = (
      <>
        <PropRow label={t("Point de relâchement")} mono>
          {formatMgrs(plume.releaseLon, plume.releaseLat)}
        </PropRow>
        {agent && <PropRow label={t("Zone de relâchement")}>{t("rayon {distance}", { distance: KM(agent.released_m) })}</PropRow>}
        {agent && <PropRow label={t("Zone dangereuse")}>{t("{distance} sous le vent, sur 30°", { distance: KM(reach) })}</PropRow>}
      </>
    );
  }

  return (
    <FloatingPanel
      enter="right"
      width="card"
      tallHeader
      icon={
        <GraphicGlyph
          family={item.family}
          kind={item.family === "cbrnPlumes" ? item.graphic.agent : item.graphic.kind}
          color={type.color}
          size={16}
        />
      }
      title={<CardTitle title={label} subtitle={term(type.name)} />}
      actions={<CardActions topic="carte.graphiques" />}
      footer={
        <CardFooter>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const view = viewOf(catalog, item);
              requestFlyTo(view.at, view.zoom);
            }}
          >
            <Crosshair size={14} /> {t("Centrer")}
          </Button>
          <Button variant="danger-ghost" size="sm" blocked={disabled} onBlocked={() => refuseIfDossierLocked() || requestSave()} onClick={() => remove(id)}>
            <Trash2 size={14} /> {t("Supprimer")}
          </Button>
        </CardFooter>
      }
    >
      <div className="space-y-3 px-4 pt-3 pb-3">
        <Field label={t("Nom")}>
          <CommitInput
            value={item.graphic.label ?? ""}
            placeholder={type.short}
            maxLength={60}
            disabled={disabled}
            onCommit={(text) =>
              edit(id, (it) => ({ ...it.graphic, label: text.replace(/\s+/g, " ").trim() || null }), t("Graphique renommé"))
            }
          />
        </Field>
        {fields}
      </div>
      <div className="border-t border-line px-4 py-1.5">{props}</div>
      <div className="border-t border-line px-4 py-3">
        {canEdit ? (
          <p className="text-meta leading-snug text-ink-muted">
            {t("Faites glisser le graphique sélectionné pour le déplacer. Chaque modification s'annule depuis l'avis qui la confirme.")}
          </p>
        ) : (
          <SaveDossierHint action="modifier le graphique" />
        )}
      </div>
    </FloatingPanel>
  );
}
